import { useEffect, useState } from "react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { IMAGES } from "@/data/site";

const fieldCls = "bg-transparent border border-charred/20 rounded-md focus-visible:ring-terracotta text-charred placeholder:text-charred/40";
const empty = { nome: "", telefono: "", email: "", esperienza: "", mansione: "", disponibilita: "", note: "", consenso: false, website: "" };

export default function Lavora() {
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [active, setActive] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/settings/jobs").then((r) => setActive(r.data.active)).catch(() => setActive(true));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consenso) {
      toast.error("Devi autorizzare il trattamento dei dati personali.");
      return;
    }
    setSending(true);
    try {
      await api.post("/applications", form);
      toast.success("Candidatura inviata. Ti ricontatteremo in caso di opportunità.");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (active === false) {
    return (
      <Layout>
        <div className="pt-[124px] md:pt-[136px] lg:pt-[172px]">
          <div className="max-w-[700px] mx-auto px-6 md:px-12 py-32 md:py-44 text-center" data-testid="lavora-closed">
            <p className="text-xs uppercase tracking-[0.25em] text-terracotta font-medium">Lavora con noi</p>
            <h1 className="mt-4 font-serif text-3xl md:text-5xl text-charred tracking-tight leading-tight">
              Al momento non ci sono posizioni aperte.
            </h1>
            <p className="mt-4 font-serif italic text-xl text-charred/60">Grazie per l'interesse verso SAPORÉ.</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (active === null) {
    return <Layout><div className="min-h-screen" /></Layout>;
  }

  return (
    <Layout>
      <div className="pt-[124px] md:pt-[136px] lg:pt-[172px]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-terracotta font-medium">Lavora con noi</p>
            <h1 className="mt-3 font-serif text-4xl md:text-6xl text-charred tracking-tight leading-none">
              Entra a far parte<br /><span className="italic text-terracotta">di SAPORÉ</span>
            </h1>
            <p className="mt-6 text-charred/70 leading-relaxed max-w-md">
              Siamo interessati a conoscere persone serie, motivate e appassionate di cucina e servizio al cliente. L'invio della candidatura non comporta alcun obbligo di assunzione e i dati saranno trattati esclusivamente per finalità di selezione del personale.
            </p>
            <div className="noise-frame mt-10 overflow-hidden rounded-[4px] aspect-[4/3] group max-w-md">
              <img src={IMAGES.chef} alt="La cucina di SAPORÉ al lavoro"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <form onSubmit={submit} className="bg-sand rounded-lg p-8 md:p-10 space-y-5" data-testid="application-form">
              <input type="text" value={form.website} onChange={(e) => set("website", e.target.value)}
                className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              <Input required placeholder="Nome e cognome *" value={form.nome} onChange={(e) => set("nome", e.target.value)} className={fieldCls} data-testid="application-name-input" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Input required placeholder="Recapito telefonico *" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} className={fieldCls} data-testid="application-phone-input" />
                <Input type="email" placeholder="E-mail" value={form.email} onChange={(e) => set("email", e.target.value)} className={fieldCls} data-testid="application-email-input" />
              </div>
              <Input placeholder="Mansione di interesse" value={form.mansione} onChange={(e) => set("mansione", e.target.value)} className={fieldCls} data-testid="application-role-input" />
              <Textarea placeholder="Esperienza professionale" value={form.esperienza} onChange={(e) => set("esperienza", e.target.value)} className={`${fieldCls} min-h-[100px]`} data-testid="application-experience-textarea" />
              <Input placeholder="Disponibilità (orari, giorni)" value={form.disponibilita} onChange={(e) => set("disponibilita", e.target.value)} className={fieldCls} data-testid="application-availability-input" />
              <Textarea placeholder="Note (puoi descrivere qui il tuo curriculum)" value={form.note} onChange={(e) => set("note", e.target.value)} className={`${fieldCls} min-h-[90px]`} data-testid="application-notes-textarea" />
              <label className="flex items-start gap-3 text-sm text-charred/80 cursor-pointer">
                <Checkbox checked={form.consenso} onCheckedChange={(v) => set("consenso", !!v)} data-testid="application-consent-checkbox" />
                Autorizzo il trattamento dei dati personali per finalità di selezione *
              </label>
              <button type="submit" disabled={sending} data-testid="application-submit-btn"
                className="w-full rounded-full bg-terracotta text-cream px-8 py-3.5 text-sm font-semibold hover:bg-terracotta-dark transition-colors disabled:opacity-60">
                {sending ? "Invio in corso…" : "Invia la candidatura"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </Layout>
  );
}
